import _ from 'lodash';
import { createContext, CSSProperties, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import './styles.css';
import KeyValue from '../../structures/keyValePair';

// Types
type ViewportSize = {
    width: number;
    height: number;
};

function useWindowSizeDebounced(delay: number = 150): ViewportSize {
    const [windowSize, setWindowSize] = useState<ViewportSize>(() => ({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    }));

    const debouncedUpdateSize = useCallback(
        _.debounce(() => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }, delay),
        [delay]
    );

    useEffect(() => {
        // Prefer ResizeObserver when available
        const controller = new AbortController();
        window.addEventListener('resize', debouncedUpdateSize, {
            signal: controller.signal,
            passive: true,
        });

        return () => {
            controller.abort();
            debouncedUpdateSize.cancel();
        };
    }, [debouncedUpdateSize]);

    return windowSize;
}

type ToggleGroupContextType = {
    activeValue: any;
    onChange: (value: any) => void;
    buttonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
};

const ToggleGroupContext = createContext<ToggleGroupContextType | undefined>(undefined);

type ToggleGroupPropsType = {
    children: ReactNode;
    activeValue: any;
    onChange: (value: any) => void;
};
function ToggleGroup({ children, activeValue, onChange }: ToggleGroupPropsType) {
    const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const [indicatorStyle, setIndicatorStyle] = useState<CSSProperties>({});
    const windowSize = useWindowSizeDebounced();

    useEffect(() => {
        if (buttonRefs.current[activeValue]) {
            const el = buttonRefs.current[activeValue];
            setIndicatorStyle({
                width: Math.floor(el.getBoundingClientRect().width) + 'px',
                left: el.offsetLeft + 'px',
            });
        }
    }, [activeValue, windowSize]);

    return (
        <ToggleGroupContext.Provider value={{ activeValue, onChange, buttonRefs }}>
            <div className="toggle-group__container">
                <div className="toggle-group__wrapper">
                    {children}
                    <span className="toggle-group__active-indicator" style={indicatorStyle} />
                </div>
            </div>
        </ToggleGroupContext.Provider>
    );
}

type ToggleButtonPropsType = {
    value: any;
    children: ReactNode;
};

ToggleGroup.Button = function ToggleButton({ value, children }: ToggleButtonPropsType) {
    const context = useContext(ToggleGroupContext);
    if (!context) {
        throw new Error('ToggleGroup.Button must be used within a ToggleGroup component');
    }

    const { activeValue, onChange, buttonRefs } = context;

    return (
        <button
            ref={(el) => (buttonRefs.current[value] = el)}
            onClick={() => onChange(value)}
            className={`toggle-group__button ${activeValue === value ? 'toggle-group__button--active' : ''}`}
        >
            {children}
        </button>
    );
};

type TogglePropsType = {
    activeValue: any;
    onChange: (value: any) => void;
    options: KeyValue[];
};

function Toggle({ activeValue, onChange, options }: TogglePropsType) {
    return (
        <ToggleGroup activeValue={activeValue} onChange={onChange}>
            {options.map((option) => (
                <ToggleGroup.Button key={option.key} value={option.key}>
                    {option.value}
                </ToggleGroup.Button>
            ))}
        </ToggleGroup>
    );
}

export default ToggleGroup;
export { Toggle };
